// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react'
import { snakeCase } from 'lodash-es'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input } from '@node-space/ui-components'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressFields, AddressType, AddressTypes } from 'pages/Contacts/types/fiatInstruments'
import { EEA_COUNTRY_CODES } from 'pages/Contacts/utils/constants.fiatInstruments'
import { BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'

type AddressFieldsProps = {
  errors: FieldErrors<ContactFiatInstrumentFormData>
  formValues: ContactFiatInstrumentFormData
  type: AddressType
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
}

export const AddressDetails = ({
  errors,
  formValues,
  type,
  clearErrors,
  register,
  setValue,
  setError,
}: AddressFieldsProps) => {
  const { t } = useTranslation()
  const { enableHybridAddress } = useFeatureFlags()

  const {
    data: countries,
    isFetching: countriesLoading,
    isError: countriesError,
  } = useFilteredCountriesQuery(true)

  const countryList = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }

    return []
  }, [countries])

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isEur = formValues?.currency === BeneficiaryDetailsCurrency.EUR
  const isSwift = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isBeneficiaryAddress = type === AddressTypes.BENEFICIARY
  const isIntermediaryBankAddress = type === AddressTypes.INTERMEDIARY_BANK
  const isBankAddress = type === AddressTypes.BANK
  const isUsdFedwireEnabled = formValues?.paymentMethods?.includes(PaymentMethods.FEDWIRE) && isUsd
  const isEurAndIbanCountryIsNonEea =
    isBeneficiaryAddress &&
    isEur &&
    !isSwift &&
    !EEA_COUNTRY_CODES.includes(formValues?.accountNumber?.slice(0, 2).toUpperCase() ?? '')

  const labels = {
    addressLine1: t('formFields.addressLine1'),
    addressLine2: t('formFields.addressLine2'),
    city: t('formFields.city'),
    postCode: t('formFields.postCode'),
    region: t('formFields.region'),
    country: t('formFields.country'),
  }

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  useEffect(() => {
    if (countriesError) {
      setError(getAddressFieldName('CountryCode'), { message: t('error.countriesError') })
    }
  }, [countriesError])

  useEffect(() => {
    const canUpdateCountryName =
      isBeneficiaryAddress && formValues?.beneficiaryCountryCode && countryList?.length

    if (canUpdateCountryName) {
      const countryName = countryList.find(
        country => country?.value === formValues?.beneficiaryCountryCode
      )?.leftText
      const countryNameField = getAddressFieldName('CountryName')
      setValue(countryNameField, countryName)
    }
  }, [isBeneficiaryAddress, formValues?.beneficiaryCountryCode, countryList])

  const isOptional = useMemo(() => {
    if (isBeneficiaryAddress) {
      if (enableHybridAddress || isSwift || isUsdFedwireEnabled || isEurAndIbanCountryIsNonEea) {
        return false
      }

      const {
        beneficiaryAddressLine1,
        beneficiaryAddressLine2,
        beneficiaryCity,
        beneficiaryPostCode,
        beneficiaryRegion,
        beneficiaryCountryCode,
      } = formValues || {}

      // If any of the beneficiary address fields are provided, the other address fields are required
      // This is to avoid partial addresses
      return !(
        beneficiaryAddressLine1 ||
        beneficiaryAddressLine2 ||
        beneficiaryCity ||
        beneficiaryPostCode ||
        beneficiaryRegion ||
        beneficiaryCountryCode
      )
    }

    return !isSwift
  }, [
    isUsd,
    isSwift,
    isBeneficiaryAddress,
    formValues,
    isUsdFedwireEnabled,
    isEurAndIbanCountryIsNonEea,
    enableHybridAddress,
  ])

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const onCountryChange = (fieldName: keyof ContactFiatInstrumentFormData, countryCode: string) => {
    setValue(fieldName, countryCode)
  }

  const getAddressFieldName = (baseName: AddressFields) => {
    return `${type}${baseName}` as keyof ContactFiatInstrumentFormData
  }

  const isNonCountryFieldsRequired = useMemo(() => {
    if (enableHybridAddress && isBeneficiaryAddress) return true

    const { paymentMethods, currency } = formValues || {}

    const isSwiftAndBeneficiaryAddress = isSwift && isBeneficiaryAddress
    const isABankAddress = isIntermediaryBankAddress || isBankAddress

    if (isSwiftAndBeneficiaryAddress || isABankAddress || isEurAndIbanCountryIsNonEea) return true

    if (currency === BeneficiaryDetailsCurrency.USD && isBeneficiaryAddress && !isSwift) {
      return (
        paymentMethods?.includes(PaymentMethods.FEDWIRE) ||
        paymentMethods?.includes(PaymentMethods.FEDNOW)
      )
    }

    return false
  }, [
    isUsdFedwireEnabled,
    isBeneficiaryAddress,
    isIntermediaryBankAddress,
    isBankAddress,
    isSwift,
    isEurAndIbanCountryIsNonEea,
    enableHybridAddress,
    formValues?.paymentMethods,
    formValues?.currency,
  ])

  return (
    <Box width="full">
      <Box flexCol width="full" gapX={12} gapY={16}>
        {isNonCountryFieldsRequired && (
          <>
            <Input
              {...register(getAddressFieldName('AddressLine1'))}
              name={getAddressFieldName('AddressLine1')}
              testId={snakeCase(getAddressFieldName('AddressLine1')?.toLowerCase())}
              label={labels?.addressLine1}
              placeholder={t('inputPlaceholders.enterAddressLine1Placeholder')}
              errorText={errors?.[getAddressFieldName('AddressLine1')]?.message}
              error={!!errors?.[getAddressFieldName('AddressLine1')]}
              onChange={handleInputChange(getAddressFieldName('AddressLine1'))}
              optionalText={isOptional && t('optional')}
            />
            <Input
              {...register(getAddressFieldName('AddressLine2'))}
              name={getAddressFieldName('AddressLine2')}
              testId={snakeCase(getAddressFieldName('AddressLine2')?.toLowerCase())}
              label={labels?.addressLine2}
              placeholder={t('inputPlaceholders.enterAddressLine2Placeholder')}
              errorText={errors?.[getAddressFieldName('AddressLine2')]?.message}
              error={!!errors?.[getAddressFieldName('AddressLine2')]}
              onChange={handleInputChange(getAddressFieldName('AddressLine2'))}
              optionalText={t('optional')}
            />

            <Box flex gapX={12}>
              <Box flexSize="auto" width="full">
                <Input
                  {...register(getAddressFieldName('City'))}
                  name={getAddressFieldName('City')}
                  testId={snakeCase(getAddressFieldName('City')?.toLowerCase())}
                  label={labels?.city}
                  placeholder={t('inputPlaceholders.enterCityPlaceholder')}
                  errorText={errors?.[getAddressFieldName('City')]?.message}
                  error={!!errors?.[getAddressFieldName('City')]}
                  onChange={handleInputChange(getAddressFieldName('City'))}
                  optionalText={isOptional && t('optional')}
                />
              </Box>
              <Box flexSize="auto" width="full">
                <Input
                  {...register(getAddressFieldName('PostCode'))}
                  name={getAddressFieldName('PostCode')}
                  label={labels?.postCode}
                  testId={snakeCase(getAddressFieldName('PostCode')?.toLowerCase())}
                  placeholder={t('inputPlaceholders.enterPostalCodePlaceholder')}
                  errorText={errors?.[getAddressFieldName('PostCode')]?.message}
                  error={!!errors?.[getAddressFieldName('PostCode')]}
                  onChange={handleInputChange(getAddressFieldName('PostCode'))}
                  optionalText={isOptional && t('optional')}
                />
              </Box>
            </Box>

            <Input
              {...register(getAddressFieldName('Region'))}
              name={getAddressFieldName('Region')}
              label={labels?.region}
              testId={snakeCase(getAddressFieldName('Region')?.toLowerCase())}
              placeholder={t('inputPlaceholders.enterRegionPlaceholder')}
              errorText={errors?.[getAddressFieldName('Region')]?.message}
              error={!!errors?.[getAddressFieldName('Region')]}
              onChange={handleInputChange(getAddressFieldName('Region'))}
              optionalText={isOptional && t('optional')}
            />
          </>
        )}

        {isBeneficiaryAddress && (
          <Combobox
            {...register(getAddressFieldName('CountryCode'))}
            testId={snakeCase(getAddressFieldName('CountryCode')?.toLowerCase())}
            label={t('country')}
            id={getAddressFieldName('CountryCode')}
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countryList}
            onChange={value => onCountryChange(getAddressFieldName('CountryCode'), value)}
            loading={countriesLoading}
            defaultValue={formValues?.[getAddressFieldName('CountryCode')] as string}
            errorText={errors?.[getAddressFieldName('CountryCode')]?.message}
          />
        )}
      </Box>
    </Box>
  )
}
